import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { buildTitleFromFilename } from '../utils/titleFormatting';

const STORAGE_KEY = 'uploadQueue:v1';

function safeJsonParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

function formatErrorMessage(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  return 'Upload failed';
}

function uploadViaXhr({ url, file, contentType, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      onProgress?.(evt.loaded, evt.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Storage upload failed (${xhr.status})`));
    };

    xhr.onerror = () => reject(new Error('Storage upload failed'));
    xhr.onabort = () => reject(Object.assign(new Error('Upload canceled'), { code: 'ABORTED' }));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort, { once: true });
      xhr.addEventListener('loadend', () => signal.removeEventListener('abort', onAbort));
    }

    xhr.send(file);
  });
}

function guessKindFromMime(mimeType) {
  const type = String(mimeType || '');
  if (type.startsWith('video/')) return 'video';
  return 'image';
}

const UploadQueueContext = createContext(null);

export function UploadQueueProvider({ children }) {
  const [queueRunNonce, setQueueRunNonce] = useState(0);
  const [items, setItems] = useState(() => {
    const restored = safeJsonParse(localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(restored)) return [];
    return restored.map((item) => {
      if (item.status === 'done' || item.status === 'error' || item.status === 'canceled') {
        return { ...item, file: null, hasFile: false };
      }
      if (item.source === 'google_drive') {
        return { ...item, file: null, hasFile: false, status: 'queued' };
      }
      return { ...item, file: null, hasFile: false, status: 'interrupted' };
    });
  });

  const activeRef = useRef({
    itemId: null,
    xhrAbortController: null,
    phaseAbortController: null,
  });

  useEffect(() => {
    const serializable = items.map((item) => ({
      id: item.id,
      batchId: item.batchId || '',
      siteId: item.siteId,
      folder: item.folder || '',
      filename: item.filename,
      title: item.title,
      kind: item.kind,
      source: item.source || 'local',
      driveFileId: item.driveFileId || null,
      driveMimeType: item.driveMimeType || null,
      driveUploadId: item.driveUploadId || null,
      size: item.size,
      status: item.status,
      loaded: item.loaded,
      total: item.total,
      key: item.key,
      error: item.error,
      createdAt: item.createdAt,
      finishedAt: item.finishedAt || null,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  }, [items]);

  const enqueueFiles = (siteId, files, options = {}) => {
    if (!siteId || !files?.length) return;
    const now = Date.now();
    const folder = typeof options?.folder === 'string' ? options.folder.trim() : '';
    const titleBase = typeof options?.titleBase === 'string' ? options.titleBase.trim() : '';
    const batchId = typeof options?.batchId === 'string' ? options.batchId.trim() : '';
    const newItems = Array.from(files).map((file, index) => {
      const id = `${now}-${Math.random().toString(16).slice(2)}`;
      const kind = file.type?.startsWith('video') ? 'video' : 'image';
      return {
        id,
        batchId,
        siteId,
        folder,
        source: 'local',
        file,
        hasFile: true,
        filename: file.name,
        title: titleBase
          ? (files.length > 1 ? `${titleBase} ${index + 1}` : titleBase)
          : buildTitleFromFilename(file.name),
        kind,
        size: file.size || 0,
        status: 'queued',
        loaded: 0,
        total: file.size || 0,
        key: null,
        error: null,
        createdAt: now,
        finishedAt: null,
      };
    });
    setItems((prev) => [...prev, ...newItems]);
    return newItems;
  };

  const enqueueGoogleDriveFiles = (siteId, files) => {
    if (!siteId || !files?.length) return;
    const now = Date.now();
    const newItems = Array.from(files).map((file) => {
      const id = `${now}-${Math.random().toString(16).slice(2)}`;
      const filename = file?.name || 'drive-file';
      const mimeType = file?.mimeType || 'application/octet-stream';
      const size = Number(file?.size) || 0;
      return {
        id,
        siteId,
        source: 'google_drive',
        file: null,
        hasFile: false,
        filename,
        title: buildTitleFromFilename(filename),
        kind: guessKindFromMime(mimeType),
        size,
        status: 'queued',
        loaded: 0,
        total: size,
        key: null,
        error: null,
        createdAt: now,
        finishedAt: null,
        driveFileId: file?.id || null,
        driveMimeType: mimeType,
        driveUploadId: null,
      };
    }).filter((x) => x.driveFileId);

    setItems((prev) => [...prev, ...newItems]);
  };

  const updateTitle = (itemId, title) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, title } : item))
    );
  };

  const clearCompleted = () => {
    setItems((prev) => prev.filter((item) => item.status !== 'done'));
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const cancelItem = async (itemId) => {
    const item = items.find((x) => x.id === itemId);
    if (!item) return;

    if (activeRef.current.itemId === itemId) {
      activeRef.current.xhrAbortController?.abort();
      activeRef.current.phaseAbortController?.abort();
    }

    setItems((prev) =>
      prev.map((x) => (x.id === itemId ? { ...x, status: 'canceled', finishedAt: Date.now() } : x))
    );

    if (item?.key) {
      try {
        const token = localStorage.getItem('token');
        await fetch('/api/uploads/abort', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ key: item.key }),
        });
      } catch (err) {
        // Best-effort cleanup only.
      }
    }

    if (item?.source === 'google_drive' && item?.driveUploadId) {
      try {
        const token = localStorage.getItem('token');
        await fetch('/api/googledrive/import-cancel', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uploadId: item.driveUploadId }),
        });
      } catch (err) {
        // Best-effort only.
      }
    }

    removeItem(itemId);
  };

  useEffect(() => {
    const hasActive = Boolean(activeRef.current.itemId);
    if (hasActive) return;
    const next = items.find((item) => item.status === 'queued');
    if (!next) return;

    const token = localStorage.getItem('token');
    const phaseController = new AbortController();
    const xhrController = new AbortController();
    activeRef.current = {
      itemId: next.id,
      phaseAbortController: phaseController,
      xhrAbortController: xhrController,
    };

    const run = async () => {
      try {
        if (next.source === 'google_drive') {
          setItems((prev) =>
            prev.map((item) => (item.id === next.id ? { ...item, status: 'presigning', error: null } : item))
          );

          const startRes = await fetch('/api/googledrive/import-file', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            signal: phaseController.signal,
            body: JSON.stringify({
              siteId: next.siteId,
              fileId: next.driveFileId,
              title: next.title,
            }),
          });
          if (!startRes.ok) {
            throw new Error(`Google Drive import failed (${startRes.status})`);
          }
          const { uploadId } = await startRes.json();
          if (!uploadId) throw new Error('Missing uploadId from Google Drive import');

          setItems((prev) =>
            prev.map((item) =>
              item.id === next.id
                ? { ...item, status: 'uploading', driveUploadId: uploadId, loaded: 0, total: item.total || 0 }
                : item
            )
          );

          const poll = async () => {
            const res = await fetch(`/api/googledrive/import-status?uploadId=${encodeURIComponent(uploadId)}`, {
              headers: { Authorization: `Bearer ${token}` },
              signal: phaseController.signal,
            });
            if (!res.ok) {
              throw new Error(`Google Drive status failed (${res.status})`);
            }
            return res.json();
          };

          while (true) {
            if (phaseController.signal.aborted) return;
            const info = await poll();
            const loaded = Number(info.loaded) || 0;
            const total = Number(info.total) || 0;
            const state = info.status;

            setItems((prev) =>
              prev.map((item) => {
                if (item.id !== next.id) return item;
                const mappedStatus = state === 'finalizing'
                  ? 'finalizing'
                  : state === 'done'
                    ? 'done'
                    : state === 'error'
                      ? 'error'
                      : state === 'canceled'
                        ? 'canceled'
                        : 'uploading';
                return {
                  ...item,
                  status: mappedStatus,
                  loaded,
                  total: total || item.total,
                  error: state === 'error' ? (info.error || 'Import failed') : null,
                };
              })
            );

            if (state === 'done') break;
            if (state === 'error') throw new Error(info.error || 'Import failed');
            if (state === 'canceled') return;
            await new Promise((resolve) => setTimeout(resolve, 750));
          }
        } else {
          setItems((prev) =>
            prev.map((item) => (item.id === next.id ? { ...item, status: 'presigning', error: null } : item))
          );

          const presignRes = await fetch(
            `/api/uploads/presign?kind=${encodeURIComponent(next.kind)}&filename=${encodeURIComponent(
              next.filename
            )}&siteId=${encodeURIComponent(next.siteId)}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              signal: phaseController.signal,
            }
          );
          if (!presignRes.ok) {
            throw new Error(`Failed to get upload URL (${presignRes.status})`);
          }
          const { url, key } = await presignRes.json();

          setItems((prev) =>
            prev.map((item) =>
              item.id === next.id
                ? { ...item, status: 'uploading', key, loaded: 0, total: next.file?.size || item.total }
                : item
            )
          );

          if (!next.file) {
            throw new Error('Missing local file (try re-adding it)');
          }

          await uploadViaXhr({
            url,
            file: next.file,
            contentType: next.file.type,
            signal: xhrController.signal,
            onProgress: (loaded, total) => {
              setItems((prev) =>
                prev.map((item) =>
                  item.id === next.id ? { ...item, loaded, total: total || item.total } : item
                )
              );
            },
          });

          setItems((prev) =>
            prev.map((item) => (item.id === next.id ? { ...item, status: 'finalizing' } : item))
          );

          const finalizeRes = await fetch('/api/uploads/finalize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            signal: phaseController.signal,
            body: JSON.stringify({
              filename: next.filename,
              fileType: next.file.type,
              key,
              siteId: next.siteId,
              title: next.title,
              folder: next.folder || null,
            }),
          });
          if (!finalizeRes.ok) {
            throw new Error(`Finalize failed (${finalizeRes.status})`);
          }
        }

        setItems((prev) =>
          prev.map((item) =>
            item.id === next.id
              ? {
                  ...item,
                  status: 'done',
                  loaded: item.total || item.loaded,
                  error: null,
                  finishedAt: Date.now(),
                }
              : item
          )
        );

        window.dispatchEvent(new CustomEvent('uploadQueue:itemDone', {
          detail: {
            batchId: next.batchId || '',
            siteId: next.siteId,
            folder: next.folder || '',
          },
        }));
      } catch (err) {
        if (err?.code === 'ABORTED' || phaseController.signal.aborted) {
          return;
        }
        setItems((prev) =>
          prev.map((item) =>
            item.id === next.id
              ? { ...item, status: 'error', error: formatErrorMessage(err), finishedAt: Date.now() }
              : item
          )
        );
      } finally {
        if (activeRef.current.itemId === next.id) {
          activeRef.current = { itemId: null, xhrAbortController: null, phaseAbortController: null };
        }
        setQueueRunNonce((value) => value + 1);
      }
    };

    run();
  }, [items, queueRunNonce]);

  const value = useMemo(
    () => ({
      items,
      enqueueFiles,
      enqueueGoogleDriveFiles,
      updateTitle,
      cancelItem,
      removeItem,
      clearCompleted,
    }),
    [items]
  );

  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}

export function useUploadQueue() {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) throw new Error('useUploadQueue must be used within UploadQueueProvider');
  return ctx;
}
